import { Alert, App, Table, Button, Input, Space, Col, Row, Tooltip, Modal, Form, Tag, InputRef } from 'antd';
import React, { FC, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserItemStore, UserGroupItem, UserItem } from 'src/stores/users-store';
import { AppConfig, container } from 'src/inversify.config';
import { ColumnsType } from 'antd/lib/table';
import { CheckSquareFilled, DeleteOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { GroupSummary, GroupType } from 'src/stores/groups-store';
import GroupsSelect from 'src/common/components/business/groups-select';
import { ENV } from 'src/';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { curateString } from 'src/core/utils/object';

const AddUserGroupsModal: FC<{
  onAddGroups: (groups: GroupSummary[]) => void;
  onClose: () => void;
  userAlreadyIsOnLicenseManagerGroup: boolean;
  exclude?: string[];
  organizationIds?: string[];
}> = (props) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<{ value: string; label: string; organization: string; organizationId: string; type: GroupType }[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();

  const onChange = (values: { value: string; label: string; organization: string; organizationId: string; type: GroupType }[]) => {
    setSelection(values);
  };

  const onAdd = () => {
    if (props.userAlreadyIsOnLicenseManagerGroup && selection.some((v) => v.type === GroupType.License)) {
      setErrorMessage(t('UserOnAddGroupAlreadyIsLicenseManagerError'));
      return;
    } else if (selection.filter((v) => v.type === GroupType.License).length > 1) {
      setErrorMessage(t('MoreThanOneLicenseManagerGroupSelectedError'));
      return;
    }
    setErrorMessage(undefined);
    props.onAddGroups(
      selection.map((x) => ({ id: x.value, name: x.label, organization: x.organization, organizationId: x.organizationId, groupType: x.type } as any))
    );
    props.onClose();
  };
  return (
    <Modal
      className="figma-modal"
      title={<h3 className="figma-modal__title">{t('AddUserToGroups')}</h3>}
      open={true}
      onOk={onAdd}
      onCancel={props.onClose}
      okButtonProps={{ disabled: selection.length === 0, style: { width: '45%', float: 'right' } }}
      cancelButtonProps={{ type: 'default', style: { width: '45%' } }}
      okText={t('AddToGroups')}
      cancelText={t('Cancel')}
    >
      {errorMessage && <Alert type="error" message={errorMessage} />}

      <Form layout="vertical" autoComplete="off">
        <Form.Item>
          <GroupsSelect onChange={onChange} exclude={props.exclude} organizationIds={props.organizationIds} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const UserGroupsTab: FC<{ userOrganizations: string[] }> = ({ userOrganizations, ...props }) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const userGroupsStore = container.get(UserItemStore);
  const userGroupsState = userGroupsStore.state;
  const [error, setError] = useState('');
  const [addGroupsVisible, setAddGroupsVisible] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([] as any[]);

  const searchInputRef = useRef<InputRef>(null);
  const { modal } = App.useApp();

  const handleSearch = (_: any, confirm: () => void) => {
    confirm();
  };

  const handleSearchReset = (clearFilters: (() => void) | undefined, confirm: (() => void) | undefined) => {
    clearFilters && clearFilters();
    confirm && confirm();
  };

  const onReload = async () => {
    await userGroupsStore.load(userGroupsState.item.value.id);
  };

  const onAddUserGroups = async (newGroups: GroupSummary[]) => {
    const response = await userGroupsStore.addUserGroups(
      userGroupsState.item.value.id,
      newGroups.map((x) => x.id)
    );
    if (response && response?.isSuccess) {
      userGroupsState.set((s) => {
        s.item.groups = [
          ...s.item.groups,
          ...newGroups
            .filter((r) => s.item.groups.every((sr) => sr.groupId !== r.id))
            .map((r) => ({
              groupId: r.id,
              name: r.name,
              organizationId: r.organizationId,
              organizationName: r.organization,
              groupType: r.groupType,
            })),
        ];
        return s;
      });
      setError('');
      userGroupsStore.clearMessages();
    } else {
      const msg = config.environment === ENV.DEV ? userGroupsState.errorMessage.value : t('ErrorAddingGroup');
      setError(msg || t('UnexpectedError'));
    }
  };

  const onDeleteUserGroups = () => {
    const groupIds = [...selectedRowKeys];
    modal.confirm({
      title: <h3>{`${t('Confirmation')}`}</h3>,
      icon: null,
      onOk: async () => {
        const response = await userGroupsStore.deleteUserGroups(userGroupsState.item.value.id, groupIds);
        if (response?.isSuccess) {
          setSelectedRowKeys([]);
          userGroupsState.set((s) => {
            s.item.groups = s.item.groups.filter((r) => !groupIds.includes(r.groupId));
            return s;
          });
          setError('');
          userGroupsStore.clearMessages();
        } else {
          const msg = config.environment === ENV.DEV ? userGroupsState.errorMessage.value : t('ErrorRemovingGroup');
          setError(msg || t('UnexpectedError'));
        }
      },
      content:
        groupIds.length === 1
          ? t('AreYouSureRemoveUserFromGroup').replace('{0}', `${groupIds.length}`)
          : t('AreYouSureRemoveUserFromGroups').replace('{0}', `${groupIds.length}`),
      okText: groupIds.length === 1 ? t('DeleteGroup') : t('DeleteGroups'),
      okButtonProps: { style: { width: 120 } },
      cancelText: t('Cancel'),
      cancelButtonProps: { type: 'default', style: { width: 120 } },
    });
  };

  const columns: ColumnsType<UserGroupItem> = [
    {
      title: t('Group'),
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInputRef}
            placeholder={t('Search')}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
              {t('Search')}
            </Button>
            <Button onClick={() => handleSearchReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
              {t('Reset')}
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => curateString(record.name.toString().toLowerCase()).includes(curateString(value.toString().toLowerCase())),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInputRef.current?.select(), 100);
        }
      },
    },
    {
      title: t('Organization'),
      dataIndex: 'organizationName',
      render: (value) => <Tag>{value}</Tag>,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div>
      <Row style={{ padding: '0 40px' }}>
        <Col lg={12} xs={24}>
          <Row gutter={[8, 0]}>
            {selectedRowKeys.length === 0 && (
              <Col>
                <h3 style={{ marginTop: 12 }}>{t('GroupMembership')}</h3>
              </Col>
            )}
            {selectedRowKeys.length !== 0 && (
              <h3 style={{ marginTop: 12 }}>
                {selectedRowKeys.length} {t('RowsSelected')}
              </h3>
            )}
            {selectedRowKeys.length !== 0 && (
              <CheckSquareFilled style={{ marginLeft: 15, marginBottom: 10, marginTop: 12, fontSize: 18, color: '#AB162B' }} />
            )}
          </Row>
        </Col>
        <Col lg={12} xs={24}>
          <Row justify="end">
            {selectedRowKeys.length !== 0 && (
              <Col>
                <Tooltip placement="top" title={selectedRowKeys.length === 1 ? t('RemoveMembership') : t('RemoveMemberships')}>
                  <Button icon={<DeleteOutlined />} onClick={onDeleteUserGroups} type="primary" style={{ marginBottom: 16, width: 56 }}></Button>
                </Tooltip>
              </Col>
            )}
            {selectedRowKeys.length === 0 && (
              <Col>
                <Tooltip placement="top" title={t('Reload')}>
                  <Button
                    className="ant-btn-secondary"
                    icon={<ReloadOutlined style={{ color: '#AB162B' }} />}
                    onClick={onReload}
                    style={{ marginBottom: 16, marginRight: 10, width: 56 }}
                  ></Button>
                </Tooltip>
                <Tooltip placement="top" title={t('AddToGroups')}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      if (userOrganizations && userOrganizations.length > 0) {
                        setAddGroupsVisible(true);
                      } else {
                        modal.warning({ title: t('UserHasNoOrganizationsGroupWarning') });
                      }
                    }}
                    style={{ marginBottom: 16, width: 56 }}
                  ></Button>
                </Tooltip>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      {error.length !== 0 && <Alert type="error" message={t(error || '')} />}
      <div>
        <Table rowKey="groupId" rowSelection={rowSelection} columns={columns} dataSource={(userGroupsState.item.value as UserItem)?.groups ?? []} />
      </div>
      {addGroupsVisible && (
        <AddUserGroupsModal
          userAlreadyIsOnLicenseManagerGroup={userGroupsState.item.value?.groups.some((v) => v.groupType === GroupType.License)}
          exclude={(userGroupsState.item.value?.groups ?? []).map((r) => r.groupId)}
          organizationIds={userGroupsState.item.value?.organizations.map((x) => x.organizationId) || []}
          onAddGroups={onAddUserGroups}
          onClose={() => setAddGroupsVisible(false)}
          {...props}
        />
      )}
    </div>
  );
};

export default UserGroupsTab;
